import React, { useState } from 'react';
import axios from 'axios';
import { X, Send, Loader2, Briefcase } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCE_FIELDS = {
  capital: {
    title: 'Capital Partner Briefing',
    intro: 'Indicate interest in anchoring or co-investing in BIG\'s founding $500M permanent-capital round, or in subsequent institutional rounds.',
    typeLabel: 'Investor Type',
    typeOptions: ['Family Office', 'GCC Family Office', 'Sovereign Wealth Fund', 'Pension Fund', 'Insurance / Reinsurer', 'Development Finance Institution', 'Infrastructure Fund', 'Endowment / Foundation', 'UHNWI', 'Strategic Capital Partner', 'Other'],
    sizeLabel: 'Indicative Commitment',
    sizeOptions: ['$5M – $25M', '$25M – $100M', '$100M – $250M', '$250M – $500M (Anchor)', '$500M+ (Cornerstone)']
  },
  sovereign: {
    title: 'Sovereign Briefing',
    intro: 'Strategic dialogue on national investment-platform design, PPP frameworks, SOE optimisation, asset recycling and industrial policy implementation. Engagements proceed under transparent public-procurement frameworks.',
    typeLabel: 'Institution',
    typeOptions: ['Ministry of Finance', 'Ministry of Energy / Power', 'Ministry of Mines', 'Ministry of Trade / Industry', 'Investment Promotion Agency', 'Sovereign Wealth Fund', 'State-Owned Enterprise', 'Central Bank', 'Office of the President / PM', 'Other'],
    sizeLabel: 'Indicative Mandate',
    sizeOptions: ['Strategic dialogue only', 'Single-asset / single-project framework', 'National infrastructure programme', 'Sector-wide reform & restructuring', 'Full country transformation platform']
  },
  corporate: {
    title: 'U.S. Corporate Partner Briefing',
    intro: 'Joint ventures, technology licensing, preferred-supplier, distribution and BOT arrangements with U.S. industrial, energy, AI, engineering and defence-adjacent corporations targeting African deployment.',
    typeLabel: 'Sector',
    typeOptions: ['Power Equipment', 'Energy & Oil/Gas', 'Mining Technology', 'Industrial Equipment', 'Aviation / Aerospace', 'Satellite & Comms', 'Cloud / Hyperscale', 'AI / Software', 'Cybersecurity', 'AgTech', 'MedTech', 'Logistics Systems', 'Water Infrastructure', 'Engineering / EPC', 'Other'],
    sizeLabel: 'Engagement Type',
    sizeOptions: ['Joint venture', 'Technology licensing', 'Preferred-supplier / distribution', 'Equity investment in JV', 'Long-term service contract', 'Build-operate-transfer', 'Export-financed procurement']
  }
};

const BriefingRequestModal = ({ isOpen, onClose, audience = 'capital' }) => {
  const cfg = AUDIENCE_FIELDS[audience] || AUDIENCE_FIELDS.capital;
  const [formData, setFormData] = useState({
    name: '', email: '', organization: '', role: '',
    audienceType: '', engagementSize: '', country: '', objective: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const reset = () => {
    setSubmitted(false);
    setErrorMsg('');
    setFormData({ name: '', email: '', organization: '', role: '', audienceType: '', engagementSize: '', country: '', objective: '' });
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg('');
    try {
      await axios.post(`${API}/contact/briefing`, { audience, ...formData });
      setSubmitted(true);
      setTimeout(reset, 4000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorMsg(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={`briefing-modal-${audience}`}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={reset} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0d1f3a] rounded-lg flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-serif text-xl font-bold text-[#1a365d]">{cfg.title}</h2>
              <p className="text-xs text-gray-500">Berkshire Investment Group</p>
            </div>
          </div>
          <button onClick={reset} className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid={`briefing-close-${audience}`}>
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {submitted ? (
            <div className="text-center py-12" data-testid={`briefing-success-${audience}`}>
              <div className="w-16 h-16 bg-[#e8f5f3] rounded-full flex items-center justify-center mx-auto mb-4">
                <Send className="w-8 h-8 text-[#2f9e8e]" />
              </div>
              <h3 className="text-xl font-semibold text-[#1a365d] mb-2">Request Received</h3>
              <p className="text-gray-600">Our team will reach out with a briefing pack.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid={`briefing-form-${audience}`}>
              <p className="text-sm text-gray-600">{cfg.intro}</p>

              {errorMsg && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{errorMsg}</div>}

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                  <input type="text" name="name" required value={formData.name} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                    placeholder="Jane Doe" data-testid={`briefing-input-name-${audience}`} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email *</label>
                  <input type="email" name="email" required value={formData.email} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                    placeholder="jane@office.com" data-testid={`briefing-input-email-${audience}`} />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Organization *</label>
                  <input type="text" name="organization" required value={formData.organization} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                    placeholder="Institution / company" data-testid={`briefing-input-org-${audience}`} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Role</label>
                  <input type="text" name="role" value={formData.role} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                    placeholder="Managing Partner, Minister, VP..." data-testid={`briefing-input-role-${audience}`} />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{cfg.typeLabel} *</label>
                  <select name="audienceType" required value={formData.audienceType} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                    data-testid={`briefing-input-type-${audience}`}>
                    <option value="">Select</option>
                    {cfg.typeOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Country / Jurisdiction</label>
                  <input type="text" name="country" value={formData.country} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                    placeholder="UAE, USA, Kenya..." data-testid={`briefing-input-country-${audience}`} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{cfg.sizeLabel} *</label>
                <select name="engagementSize" required value={formData.engagementSize} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                  data-testid={`briefing-input-size-${audience}`}>
                  <option value="">Select</option>
                  {cfg.sizeOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Objective *</label>
                <textarea name="objective" required rows={4} value={formData.objective} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e] resize-none"
                  placeholder="Briefly describe your interest, focus areas, time horizon..."
                  data-testid={`briefing-input-objective-${audience}`} />
              </div>

              <button type="submit" disabled={submitting}
                className="w-full bg-[#0d1f3a] hover:bg-[#1a365d] text-white py-3.5 rounded-lg font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-70"
                data-testid={`briefing-submit-${audience}`}>
                {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending...</> : <><Send className="w-5 h-5" /> Request Briefing</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default BriefingRequestModal;
